import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Space } from "antd";
import {
  DotVerticalIcon,
  DownloadIcon,
  SendIcon,
  UserPlusIcon,
  UserViewIcon,
} from "assets/icons";
import moment from "moment/moment";
import snakecaseKeys from "snakecase-keys";
import { AuthInfo, getWorkspaceId } from "stores/slices/auth";
import {
  doDeleteBroadcastList,
  doGetBroadcastListPage,
  getPaginationBroadcastList,
} from "stores/slices/broadcastList";
import { doGetListUpload } from "stores/slices/broadcastList";
import { doExportCSVCustomersByBroadcastList } from "stores/slices/customer";
import { doAddCusomtersToBroadcastList } from "stores/slices/customer";
import { doGetAllCustomField } from "stores/slices/customField";
import { changeLayout, setIsRefresh } from "stores/slices/layout";
import { doGetUserInfo, getUserDetail } from "stores/slices/user";

import AppButton from "components/button";
import LoadingProgress from "components/forms/components/loadingProgress";
import ModalForm from "components/forms/components/modalForm";
import AppTable from "components/table";

import { USER_TYPE } from "../../constants/appConstant";

import BroadcastListForm from "./form/broadcastListForm";

const BroadcastListTable = ({ isOperator }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isOpenFrm, setIsOpenFrm] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isData, setIsData] = useState(false);
  const [textConfirm, setTextConfirm] = useState("");
  const [ids, setIds] = useState();
  const [isUpdate, setIsUpdate] = useState(false);
  const [columns, setColumns] = useState([]);

  const [percent, setPercent] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [isFail, setIsFail] = useState(false);
  const [isOpenModalProgressing, setIsOpenModalProgressing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [broadcastListSelected, setBroadcastListSelected] = useState(null);
  const [isExporting, setExporting] = useState(false);
  const pagination = useSelector(getPaginationBroadcastList);
  const { pageNumber, numberOfElements } = pagination;

  const [searchParam, setSearchParam] = useState({
    sortFieldBroadcastList: "createdTime",
    sortDirection: "DESC",
    pageNumber: 1,
    pageSize: 10,
    statuses: ["ACTIVE", "INACTIVE", "PENDING"],
  });

  const broadcastListUpload = useSelector(
    state => state.broadcastList.broadcastListUpload
  );
  const workspaceId = useSelector(getWorkspaceId);
  const authInfo = useSelector(AuthInfo);
  const userInfo = useSelector(getUserDetail);

  useEffect(() => {
    if (location.state && location.state.isAdd) {
      setIsCreate(true);
      setIsOpenFrm(true);
      location.state = {};
    }
  }, [location]);

  useEffect(() => {
    if (authInfo.type === USER_TYPE.USER) {
      dispatch(doGetAllCustomField(workspaceId));
      dispatch(doGetUserInfo(authInfo.id));
    }
    generateTableColumns();
  }, []);

  useEffect(() => {
    if (broadcastListUpload.length > 0) {
      const request = broadcastListUpload[0];
      handleUploadBroadcastList(request);
    }
  }, [broadcastListUpload]);

  const handleUploadBroadcastList = async data => {
    const { payload } = await dispatch(
      doAddCusomtersToBroadcastList({
        workspaceId: data.workspaceId,
        broadcastListId: data.broadcastListId,
        createCustomerAndContactRequests: data.list.filter(
          (item, index) => index <= 99 && item
        ),
      })
    );
    if (!payload) {
      setIsDone(false);
      setIsFail(true);
    }
    if (payload.status === 200) {
      const count = data.count - 1;

      setPercent(
        Math.round(((data.totalCount - count) / data.totalCount) * 100)
      );
      if (count === 0) {
        await dispatch(
          doGetListUpload({
            broadcastListUpload: broadcastListUpload.slice(1),
          })
        );
        setIsDone(true);
      } else {
        let newList = broadcastListUpload;
        newList = newList.map(item => {
          if (item.broadcastListId === data.broadcastListId) {
            item = {
              ...item,
              list: data.list.slice(100),
              count: data.count - 1,
            };
          }
          return item;
        });
        await dispatch(
          doGetListUpload({
            broadcastListUpload: newList,
          })
        );
      }
    } else {
      setIsDone(false);
      setIsFail(true);
    }
  };

  useEffect(() => {
    if (isDone) {
      setTimeout(() => {
        setIsDone(false);
        setPercent(0);
        setIsOpenModalProgressing(false);
      }, [1000]);
      if (location.pathname.split("/")[2] === "broadcast-list") {
        dispatch(setIsRefresh(uuid()));
      }
    }
  }, [isDone]);

  useEffect(() => {
    if (isFail) {
      setTimeout(() => {
        setIsDone(false);
        setPercent(0);
        setIsOpenModalProgressing(false);
      }, [3000]);
      if (location.pathname.split("/")[2] === "broadcast-list") {
        dispatch(setIsRefresh(uuid()));
      }
    }
  }, [isFail]);

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols = [
        {
          title: t("label.workspace"),
          width: 80,
          dataIndex: "workspaceName",
          key: "workspaceName",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("label.workspace").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
          render: (_, { workspace }) => <>{workspace.name}</>,
        },
        {
          title: t("broadcastList.title.listName"),
          width: 60,
          dataIndex: "name",
          key: "name",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " +
            t("broadcastList.title.listName").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
        },

        {
          title: t("broadcastList.title.activeCustomers"),
          width: 40,
          dataIndex: "numberOfCustomer",
          key: "numberOfCustomer",
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("broadcastList.title.lastBroadcast"),
          width: 60,
          dataIndex: "lastBroadcastTime",
          key: "lastBroadcastTime",
          render: (_, { lastBroadcastTime }) => (
            <>
              {lastBroadcastTime
                ? handleFormatDateLocale(lastBroadcastTime)
                : ""}
            </>
          ),
        },
        {
          title: t("noticeBoard.actions"),
          key: "operation",
          width: 25,
          render: (_, record) => {
            return (
              <>
                <Space size="middle">
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: actionOptionItems(record),
                    }}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      shape="circle"
                      size="middle"
                      disabled={isExporting}
                    >
                      <DotVerticalIcon />
                    </Button>
                  </Dropdown>
                </Space>
              </>
            );
          },
        },
      ];
    } else {
      cols = [
        {
          title: t("broadcastList.title.listName"),
          width: 60,
          dataIndex: "name",
          key: "name",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " +
            t("broadcastList.title.listName").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("broadcastList.title.activeCustomers"),
          width: 40,
          dataIndex: "numberOfCustomer",
          key: "numberOfCustomer",
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("broadcastList.title.lastBroadcast"),
          width: 60,
          dataIndex: "lastBroadcastTime",
          key: "lastBroadcastTime",
          render: (_, { lastBroadcastTime }) => (
            <>
              {lastBroadcastTime
                ? handleFormatDateLocale(lastBroadcastTime)
                : ""}
            </>
          ),
        },
        {
          title: t("noticeBoard.actions"),
          key: "operation",
          width: 20,
          render: (_, record) => {
            return (
              <>
                <Space size="middle">
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: actionOptionItems(record),
                    }}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      shape="circle"
                      size="middle"
                      disabled={isExporting}
                    >
                      <DotVerticalIcon />
                    </Button>
                  </Dropdown>
                </Space>
              </>
            );
          },
        },
      ];
    }
    setColumns(cols);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    let newParams = {};
    switch (dataIndex) {
      case "name":
        newParams = {
          ...searchParam,
          searchKeyName: selectedKeys[0]
            ? selectedKeys[0].trim()
            : selectedKeys[0],
        };
        break;
      case "description":
        newParams = {
          ...searchParam,
          searchKeyDescription: selectedKeys[0]
            ? selectedKeys[0].trim()
            : selectedKeys[0],
        };
        break;
      case "workspaceName":
        newParams = {
          ...searchParam,
          searchKeyWorkspaceName: selectedKeys[0]
            ? selectedKeys[0].trim()
            : selectedKeys[0],
        };
        break;
    }
    newParams = { ...newParams, pageNumber: 1 };
    setSearchParam({ ...newParams, pageNumber: 1 });
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleDelete = record => {
    // setIsDeleting(true);
    setIsOpenModalDelete(true);
    setTextConfirm(record.name);
    setIds(record.id);
  };
  const handleSubmitDelete = async () => {
    const { payload } = await dispatch(doDeleteBroadcastList({ id: ids }));
    setIsOpenModalDelete(false);
    setIds();
    if (payload && payload.status === 200) {
      if (pageNumber > 1 && numberOfElements === 1) {
        setSearchParam({
          ...searchParam,
          pageNumber: pageNumber - 1,
        });
      }
      dispatch(setIsRefresh(uuid()));
      setIsDeleting(false);
    } else {
      setIsDeleting(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...searchParam,
        sortFieldBroadcastList: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...searchParam,
        sortFieldBroadcastList: "createdTime",
        sortDirection: "DESC",
      };
    }
    newSearchParam = {
      ...newSearchParam,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      statuses: ["ACTIVE", "INACTIVE", "PENDING"],
    };
    setSearchParam(newSearchParam);
  };

  const handleFormatDateLocale = date => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date)
        .tz(userInfo?.timeZone || moment.tz.guess())
        .locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const actionOptionItems = record => {
    let items = [
      {
        key: "1",
        label: <span>{t("broadcastList.label.viewBroadcastList")}</span>,
        icon: <EyeOutlined style={{ fontSize: 20, marginInlineEnd: 0 }} />,
        onClick: () => {
          if (isOperator) {
            navigate(`/broadcast-list/${record.id}`, {
              state: {
                isDefault: record.isDefault,
              },
            });
          } else {
            navigate(`/admin/broadcast-list/${record.id}`, {
              state: {
                isDefault: record.isDefault,
                broadcastListName: record.name,
                workspaceId: record.workspace.id,
                workspaceName: record.workspace.name,
              },
            });
          }
        },
      },
      {
        key: "2",
        label: <span>{t("broadcastList.label.viewCustomers")}</span>,
        icon: <UserViewIcon />,
        onClick: () => {
          if (isOperator) {
            navigate(
              `/customer/all-customer/${record.isDefault ? "all" : record.id}`,
              {
                state: {
                  name: record.isDefault ? "" : record.name,
                },
              }
            );
          } else {
            navigate(
              `/admin/customer/all-customer/${
                record.isDefault ? "all" : record.id
              }`,
              {
                state: {
                  name: record.isDefault ? "" : record.name,
                  workspaceId: record.workspace.id,
                  workspaceName: record.workspace.name,
                  broadcastListName: record.name,
                  broadcastListId: record.id,
                },
              }
            );
          }
        },
      },
      {
        key: "3",
        label: <span>{t("broadcastList.label.editBroadcastList")}</span>,
        icon: <EditOutlined style={{ fontSize: 20, marginInlineEnd: 0 }} />,
        disabled: record.isDefault,
        onClick: () => {
          setBroadcastListSelected({ ...record, list: [] });
          setIsOpenFrm(true);
          setIsCreate(false);
          setIsUpload(true);
          setIsUpdate(true);
        },
      },
      {
        key: "4",
        label: <span>{t("broadcastList.label.exportBroadcastList")}</span>,
        icon: <DownloadIcon />,
        loading: isExporting,
        disabled: isExporting,
        onClick: () => {
          setBroadcastListSelected(record);
          handleDownloadCustomer(record);
        },
      },
      {
        key: "5",
        label: <span>{t("broadcastList.label.viewMessages")}</span>,
        // disabled: !record.lastIpAddress,
        icon: <UserPlusIcon />,
        disabled: record.isDefault || isExporting,
        onClick: () => {
          setBroadcastListSelected(record);
          navigate("/admin/past");
        },
      },
      {
        key: "6",
        label: <span>{t("broadcastList.label.sendMessage")}</span>,
        icon: <SendIcon />,
        disabled: isExporting || userInfo?.type === USER_TYPE.SUPER_ADMIN,
        onClick: () => {
          setBroadcastListSelected(record);
          dispatch(changeLayout("OPERATOR"));
          navigate("/send-message");
        },
      },
      {
        key: "7",
        danger: true,
        label: <span>{t("broadcastList.label.deleteBroadcastList")}</span>,
        disabled: record.isDefault || isExporting,
        icon: <DeleteOutlined style={{ fontSize: 20, margin: 0 }} />,
        onClick: () => {
          handleDelete(record);
        },
      },
    ];
    return items;
  };
  const handleDownloadCustomer = async data => {
    setExporting(true);
    await dispatch(
      doExportCSVCustomersByBroadcastList({
        id: data.id,
        fileName: data.name,
        workspaceId: authInfo.type === USER_TYPE.USER ? "" : data.workspace.id,
      })
    );
    setExporting(false);
  };
  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("broadcastList.title.deleteBroadcastList")}
        </span>
      </div>
    );
  };

  return (
    <div style={broadcastListStyles.mainContainer}>
      {!isOperator && (
        <div style={broadcastListStyles.buttonContainer}>
          <AppButton
            label={t("button.add")}
            color={"primary"}
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setIsOpenFrm(true);
              setBroadcastListSelected(null);
              setIsUpload(false);
              setIsCreate(true);
            }}
            disabled={isExporting}
            style={broadcastListStyles.buttonAdd}
          />
        </div>
      )}
      <div>
        <AppTable
          api={
            authInfo.type === USER_TYPE.USER
              ? doGetBroadcastListPage(
                  snakecaseKeys({
                    ...searchParam,
                    workspaceId:
                      authInfo.type === USER_TYPE.USER ? workspaceId : "",
                  })
                )
              : doGetBroadcastListPage(
                  snakecaseKeys({
                    ...searchParam,
                  })
                )
          }
          columns={columns}
          searchParam={searchParam}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          handleTableChange={handleTableChange}
          setSearchParam={setSearchParam}
        />
      </div>
      <ModalForm
        title={
          isCreate
            ? t("broadcastList.title.addNewBroadcastList")
            : t("broadcastList.title.updateBroadcastList")
        }
        onCancel={() => {
          setIsOpenFrm(false);
          setIsData(false);
          setIsUpdate(false);
        }}
        onSubmit={() => setIsOpenFrm(false)}
        isOpen={isOpenFrm}
        labelSubmit={t("label.save")}
        customWidth={isCreate && isData ? 1300 : 700}
        footer={false}
        destroyOnClose={true}
        maskClosable={false}
      >
        <BroadcastListForm
          onCancel={() => {
            setIsOpenFrm(false);
            setIsCreate(false);
            setIsData(false);
            setIsUpdate(false);
          }}
          broadcastListSelected={broadcastListSelected}
          isUpload={isUpload}
          setIsUpload={setIsUpload}
          isCreate={isCreate}
          workspaceId={workspaceId}
          isUpdate={isUpdate}
          setIsRefresh={setIsRefresh}
          setIsData={setIsData}
          setIsUpdate={setIsUpdate}
          setIsOpenModalProgressing={setIsOpenModalProgressing}
          userType={authInfo.type}
        />
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalDelete}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalDelete(false);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalDelete(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.delete")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleSubmitDelete();
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("broadcastList.text.confirmDelete", { value: textConfirm })}
        </div>
      </ModalForm>
      <ModalForm
        closeIcon={false}
        title={t("title.progressing")}
        isOpen={isOpenModalProgressing}
        onCancel={() => {
          if (isDone || isFail) {
            setIsDone(false);
            setPercent(0);
            setIsOpenModalProgressing(false);
            setIsFail(false);
          }
        }}
        footer={false}
      >
        <LoadingProgress percent={percent} status={isFail ? "exception" : ""} />
        {isDone && (
          <div
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 20,
              color: "#52c41a",
              marginTop: 10,
            }}
          >
            {t("label.done")}
          </div>
        )}
        {!isDone && !isFail && (
          <div
            style={{
              textAlign: "center",
              fontSize: 14,
              marginTop: 10,
            }}
          >
            {t("text.inProgressing")}
          </div>
        )}
        {isFail && !isDone && (
          <div
            style={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: 14,
              color: "#ff4d4f",
              marginTop: 10,
            }}
          >
            {t("text.failImportCustomer")}
          </div>
        )}
      </ModalForm>
    </div>
  );
};

const broadcastListStyles = {
  mainContainer: {
    height: "100%",
    position: "relative",
  },
  buttonContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "end",
    marginBottom: 24,
  },
  buttonAdd: {
    minWidth: "auto",
    marginLeft: "10px",
  },
};
export default BroadcastListTable;
